import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { withFormik } from 'formik';
import ajax from 'sma.common/ajax';
import { Box } from '@wds/box';
import { Button } from '@wds/button';
import { Radio } from '@wds/radio';
import { FormGroup } from '@wds/form-group';
import { Typography } from '@wds/typography';
import { Select } from '@wds/select';
import options from '../support_options';
import FormikInput, { fieldTypes } from './components/formikInput';
import createHelpdeskFormSchema from './formSchema';
import DropZoneArea from './components/dropZoneArea';

function arrayBufferToBase64(buffer) {
	let binary = '';
	const bytes = new Uint8Array(buffer);
	const len = bytes.byteLength;
	for (let i = 0; i < len; i++) {
		binary += String.fromCharCode(bytes[i]);
	}
	return window.btoa(binary);
}

const readAndEncodeFileAsync = file => new Promise((resolve, reject) => {
	const reader = new FileReader();

	reader.onload = () => {
		resolve({
			name: file.name,
			content: arrayBufferToBase64(reader.result),
		});
	};

	reader.onerror = reject;

	reader.readAsArrayBuffer(file);
});

const formikEnhancer = withFormik({
	validationSchema: props => createHelpdeskFormSchema(props),
	handleSubmit: async (payload, { setSubmitting, props }) => {
		const {
			userType,
			requestType,
			categorization,
			browserVersion,
			successCallback,
		} = props;
		const newPayload = { ...payload }; // Spread operator is used here to shallow copy the object
		newPayload.description = `${newPayload.description}\n \n \n`;
		if (newPayload.relatedPrograms) {
			newPayload.description = `${newPayload.description}\nRelated Programs: ${newPayload.relatedPrograms}`;
		}
		if (newPayload.relatedStages) {
			newPayload.description = `${newPayload.description}\nRelated Stages: ${newPayload.relatedStages}`;
		}
		if (newPayload.relatedTasks) {
			newPayload.description = `${newPayload.description}\nRelated Tasks: ${newPayload.relatedTasks}`;
		}
		if (newPayload.relatedUsers) {
			newPayload.description = `${newPayload.description}\nRelated Users: ${newPayload.relatedUsers}`;
		}
		if (newPayload.relatedApplications) {
			newPayload.description = `${newPayload.description}\nRelated Applications: ${newPayload.relatedApplications}`;
		}
		if (newPayload.relatedReports) {
			newPayload.description = `${newPayload.description}\nRelated Reports: ${newPayload.relatedReports}`;
		}
		if (newPayload.relatedAutomations) {
			newPayload.description = `${newPayload.description}\nRelated Automations: ${newPayload.relatedAutomations}`;
		}
		if (newPayload.scheduleCallOption === 'scheduleCallOption2') {
			newPayload.description = `${newPayload.description}\n\nRequested call back date/times:\n`
				+ `${newPayload.scheduledDate1}, ${newPayload.scheduledTime1} EST\n${newPayload.scheduledDate2}, `
				+ `${newPayload.scheduledTime2} EST\n${newPayload.scheduledDate3}, ${newPayload.scheduledTime3} EST`;
		}
		if (newPayload.sslDomainName) {
			newPayload.subject = `CSR Request for ${newPayload.sslDomainName}`;
			newPayload.description = `${newPayload.description}\n\nCSR Information:\n`
				+ `Domain Name: ${newPayload.sslDomainName}\nCountry: ${newPayload.sslCountry}\n `
				+ `State/Province/Region: ${newPayload.sslState}\nCity: ${newPayload.sslCity}\n`
				+ `Organization Name: ${newPayload.sslOrganizationName}\nDepartment: ${newPayload.sslDepartmentName}\n`
				+ `Email: ${newPayload.sslEmail}\n`;
		}
		newPayload.files = await Promise.all(newPayload.files.map(file => readAndEncodeFileAsync(file.fileInfo)));
		const data = {
			...newPayload,
			userType,
			requestType,
			categorization,
			browserVersion,
		};
		ajax.post({
			url: reverse('helpdesk'),
			data,
			callback: () => {
				setSubmitting(false);
				successCallback();
			},
			onFail: () => {
				setSubmitting(false);
			},
			alertOnSuccess: false,
		});
	},
	mapPropsToValues: () => ({
		firstName: '',
		lastName: '',
		email: '',
		subject: '',
		description: '',
		isRequestingPhone: false,
		phoneNum: '',
		phoneExt: '',
		scheduleCallOption: 'scheduleCallOption1',
		scheduledDate1: '',
		scheduledDate2: '',
		scheduledDate3: '',
		scheduledTime1: '',
		scheduledTime2: '',
		scheduledTime3: '',
		relatedPrograms: '',
		relatedStages: '',
		relatedTasks: '',
		relatedUsers: '',
		relatedApplications: '',
		relatedReports: '',
		relatedAutomations: '',
		sslDomainName: '',
		sslCountry: '',
		sslState: '',
		sslCity: '',
		sslOrganizationName: '',
		sslDepartmentName: '',
		sslEmail: '',
		files: [],
	}),
	displayName: 'HelpdeskForm',
});


const HelpdeskForm = (props) => {
	const {
		isEnterprisePlus,
		callEligible,
		fieldsToDisplay,
		values,
		touched,
		errors,
		dirty,
		handleChange,
		handleBlur,
		handleSubmit,
		isSubmitting,
		setFieldValue,
	} = props;

	const [disableSubmit, setDisableSubmit] = useState(true);

	const hasErrors = !!(
		errors.firstName
		|| errors.lastName
		|| errors.email
		|| errors.subject
		|| errors.description
		|| errors.isRequestingPhone
		|| errors.phoneNum
		|| errors.phoneExt
		|| errors.scheduleCallOption
		|| errors.scheduledDate1
		|| errors.scheduledDate2
		|| errors.scheduledDate3
		|| errors.scheduledTime1
		|| errors.scheduledTime2
		|| errors.scheduledTime3
		|| errors.relatedPrograms
		|| errors.relatedStages
		|| errors.relatedTasks
		|| errors.relatedUsers
		|| errors.relatedApplications
		|| errors.relatedReports
		|| errors.relatedAutomations
		|| errors.sslDomainName
		|| errors.sslCountry
		|| errors.sslState
		|| errors.sslCity
		|| errors.sslOrganizationName
		|| errors.sslDepartmentName
		|| errors.sslEmail
	);

	useEffect(() => {
		setDisableSubmit(isSubmitting || !dirty || hasErrors);
	}, [dirty, errors, isSubmitting]);

	return (
		<form onSubmit={handleSubmit}>
			<FormGroup>
				{fieldsToDisplay.relatedPrograms && (
					<FormikInput
						fieldType={fieldTypes.input}
						id="relatedPrograms"
						type="text"
						label={gettext('Provide an example of a program')}
						error={!!touched.relatedPrograms && errors.relatedPrograms}
						value={values.relatedPrograms}
						onChange={handleChange}
						onBlur={handleBlur}
						required={fieldsToDisplay.relatedPrograms.required}
					/>
				)}
				{fieldsToDisplay.relatedStages && (
					<FormikInput
						fieldType={fieldTypes.input}
						id="relatedStages"
						type="text"
						label={gettext('Provide an example of a stage')}
						error={!!touched.relatedStages && errors.relatedStages}
						value={values.relatedStages}
						onChange={handleChange}
						onBlur={handleBlur}
						required={fieldsToDisplay.relatedStages.required}
					/>
				)}
				{fieldsToDisplay.relatedTasks && (
					<FormikInput
						fieldType={fieldTypes.input}
						id="relatedTasks"
						type="text"
						label={gettext('Provide an example of a task')}
						error={!!touched.relatedTasks && errors.relatedTasks}
						value={values.relatedTasks}
						onChange={handleChange}
						onBlur={handleBlur}
						required={fieldsToDisplay.relatedTasks.required}
					/>
				)}
				{fieldsToDisplay.relatedUsers && (
					<FormikInput
						fieldType={fieldTypes.input}
						id="relatedUsers"
						type="text"
						label={gettext('Provide an example of a user')}
						error={!!touched.relatedUsers && errors.relatedUsers}
						value={values.relatedUsers}
						onChange={handleChange}
						onBlur={handleBlur}
						required={fieldsToDisplay.relatedUsers.required}
					/>
				)}
				{fieldsToDisplay.relatedApplications && (
					<FormikInput
						fieldType={fieldTypes.input}
						id="relatedApplications"
						type="text"
						label={gettext('Provide an example of an application')}
						error={!!touched.relatedApplications && errors.relatedApplications}
						value={values.relatedApplications}
						onChange={handleChange}
						onBlur={handleBlur}
						required={fieldsToDisplay.relatedApplications.required}
					/>
				)}
				{fieldsToDisplay.relatedReports && (
					<FormikInput
						fieldType={fieldTypes.input}
						id="relatedReports"
						type="text"
						label={gettext('Provide an example of a report')}
						error={!!touched.relatedReports && errors.relatedReports}
						value={values.relatedReports}
						onChange={handleChange}
						onBlur={handleBlur}
						required={fieldsToDisplay.relatedReports.required}
					/>
				)}
				{fieldsToDisplay.relatedAutomations && (
					<FormikInput
						fieldType={fieldTypes.input}
						id="relatedAutomations"
						type="text"
						label={gettext('Provide an example of an automation')}
						error={!!touched.relatedAutomations && errors.relatedAutomations}
						value={values.relatedAutomations}
						onChange={handleChange}
						onBlur={handleBlur}
						required={fieldsToDisplay.relatedAutomations.required}
					/>
				)}
				{fieldsToDisplay.nameFields && (
					<div>
						<div className="d-flex">
							<FormikInput
								fieldType={fieldTypes.input}
								id="firstName"
								type="text"
								containerClassName="sma-name-input"
								inputClassName="w-100"
								label={gettext('First Name')}
								error={touched.firstName && errors.firstName}
								value={values.firstName}
								onChange={handleChange}
								onBlur={handleBlur}
								required
							/>
							<FormikInput
								fieldType={fieldTypes.input}
								id="lastName"
								type="text"
								containerClassName="sma-name-input ml-auto"
								inputClassName="w-100"
								label={gettext('Last Name')}
								error={touched.lastName && errors.lastName}
								value={values.lastName}
								onChange={handleChange}
								onBlur={handleBlur}
								required
							/>
						</div>
						<FormikInput
							fieldType={fieldTypes.input}
							id="email"
							type="text"
							label={gettext('Email')}
							error={touched.email && errors.email}
							value={values.email}
							onChange={handleChange}
							onBlur={handleBlur}
							required
						/>
					</div>
				)}
				{!fieldsToDisplay.sslFields && (
					<div>
						<FormikInput
							fieldType={fieldTypes.input}
							id="subject"
							type="text"
							label={gettext('Subject')}
							error={!!touched.subject && errors.subject}
							value={values.subject}
							onChange={handleChange}
							onBlur={handleBlur}
							required
						/>
						<FormikInput
							fieldType={fieldTypes.textArea}
							id="description"
							type="text"
							inputClassName="w-100"
							rows="10"
							label={gettext('Description of problem')}
							error={touched.description && errors.description}
							value={values.description}
							onChange={handleChange}
							onBlur={handleBlur}
							required
						/>
						<Box mb={2}>
							<Typography variant="bodySm" component="em">
								{gettext('Please do not include any sensitive data, such as credit card numbers or passwords')}
							</Typography>
						</Box>
						<DropZoneArea onFileSuccess={acceptedFiles => setFieldValue('files', acceptedFiles)} />
					</div>
				)}
				{callEligible && (
					<Box mb={4}>
						<FormikInput
							fieldType={fieldTypes.checkbox}
							id="isRequestingPhone"
							value={values.isRequestingPhone}
							label={gettext('I would like to request a call back')}
							onChange={handleChange}
						/>
						{/* eslint-disable-next-line react/no-danger */}
						<p dangerouslySetInnerHTML={
							{
								__html: gettext('(Phone support hours: <strong>Monday to Friday 9am - 7pm '
									+ 'Eastern Time</strong>, excluding holidays)'),
							}
						}
						/>
						{values.isRequestingPhone && (
							<div>
								<Box display="flex" ml={4} gap={3} mb={2} id="sma-helpdesk-phonenumber-block">
									<Box id="phoneNum-wrapper">
										<FormikInput
											fieldType={fieldTypes.input}
											id="phoneNum"
											type="tel"
											containerClassName="sma-phone-num"
											label={gettext('Phone number')}
											error={touched.phoneNum && errors.phoneNum}
											value={values.phoneNum}
											onChange={handleChange}
											onBlur={handleBlur}
											required
										/>
									</Box>
									<Box id="phoneExt-wrapper">
										<FormikInput
											fieldType={fieldTypes.input}
											id="phoneExt"
											type="tel"
											containerClassName="sma-phone-ext"
											label={gettext('Extension')}
											error={touched.phoneExt && errors.phoneExt}
											value={values.phoneExt}
											onChange={handleChange}
											onBlur={handleBlur}
										/>
									</Box>
								</Box>
								{isEnterprisePlus && (
									<div>
										<Box mb={2}>
											<Radio
												name="scheduleCallOption"
												id="scheduleCallOption1"
												value="scheduleCallOption1"
												checked={values.scheduleCallOption === 'scheduleCallOption1'}
												onChange={handleChange}
												onBlur={handleBlur}
												label={gettext('Call me back within the next hour')}
											/>
										</Box>
										<Box mb={2}>
											<Radio
												name="scheduleCallOption"
												id="scheduleCallOption2"
												value="scheduleCallOption2"
												checked={values.scheduleCallOption === 'scheduleCallOption2'}
												onChange={handleChange}
												onBlur={handleBlur}
												label={gettext('Schedule a call')}
											/>
										</Box>
										{values.scheduleCallOption === 'scheduleCallOption2' && (
											<Box ml={4}>
												<strong>
													{gettext('Provide us with the best times to reach you for a call')}
												</strong>
												<p>
													{gettext('We will make every attempt to schedule a call for one '
														+ 'of these times, but if we can\'t, we will contact you for '
														+ 'other possible times.')}
												</p>
												<div className="sma-helpdesk-scheduling">
													<div />
													<strong>
														{gettext('Date')}
													</strong>
													<div>
														<strong>
															{gettext('Time')}
														</strong>
														<span>
															{gettext(' (EST)')}
														</span>
													</div>
													<strong>
														{gettext('First choice')}
													</strong>
													<FormikInput
														fieldType={fieldTypes.input}
														id="scheduledDate1"
														className="w-100 h-100"
														type="text"
														error={!!touched.scheduledDate1 && errors.scheduledDate1}
														value={values.scheduledDate1}
														onChange={handleChange}
														onBlur={handleBlur}
														required
													/>
													<FormikInput
														fieldType={fieldTypes.input}
														id="scheduledTime1"
														className="w-100 h-100"
														type="text"
														error={!!touched.scheduledTime1 && errors.scheduledTime1}
														value={values.scheduledTime1}
														onChange={handleChange}
														onBlur={handleBlur}
														required
													/>
													<strong>
														{gettext('Second choice')}
													</strong>
													<FormikInput
														fieldType={fieldTypes.input}
														id="scheduledDate2"
														className="w-100 h-100"
														type="text"
														error={!!touched.scheduledDate2 && errors.scheduledDate2}
														value={values.scheduledDate2}
														onChange={handleChange}
														onBlur={handleBlur}
														required
													/>
													<FormikInput
														fieldType={fieldTypes.input}
														id="scheduledTime2"
														className="w-100 h-100"
														type="text"
														error={!!touched.scheduledTime2 && errors.scheduledTime2}
														value={values.scheduledTime2}
														onChange={handleChange}
														onBlur={handleBlur}
														required
													/>
													<strong>
														{gettext('Third choice')}
													</strong>
													<FormikInput
														fieldType={fieldTypes.input}
														id="scheduledDate3"
														className="w-100 h-100"
														type="text"
														error={!!touched.scheduledDate3 && errors.scheduledDate3}
														value={values.scheduledDate3}
														onChange={handleChange}
														onBlur={handleBlur}
														required
													/>
													<FormikInput
														fieldType={fieldTypes.input}
														id="scheduledTime3"
														className="w-100 h-100"
														type="text"
														error={!!touched.scheduledTime3 && errors.scheduledTime3}
														value={values.scheduledTime3}
														onChange={handleChange}
														onBlur={handleBlur}
														required
													/>
												</div>
											</Box>
										)}
									</div>
								)}
							</div>
						)}
					</Box>
				)}
				{fieldsToDisplay.sslFields && (
					<div>
						<div>
							<FormikInput
								fieldType={fieldTypes.input}
								id="sslDomainName"
								type="text"
								className="wds-input w-100"
								inputClassName="w-100"
								label={gettext('Custom URL')}
								error={touched.sslDomainName && errors.sslDomainName}
								value={values.sslDomainName}
								onChange={handleChange}
								onBlur={handleBlur}
								required
							/>
							<div className="d-flex">
								<div className="wds-form-group__field sma-name-input">
									<Select
										id="sslCountry"
										name="sslCountry"
										label={gettext('Country')}
										error={touched.sslCountry && errors.sslCountry}
										value={values.sslCountry}
										onChange={handleChange}
										onBlur={handleBlur}
									>
										{
											options.countries.map((option, idx) => (
												<Select.Option key={idx} value={option.value}>{option.string}</Select.Option>
											))
										}
									</Select>
								</div>
								<div className="sma-name-input ml-auto">
									<FormikInput
										fieldType={fieldTypes.input}
										id="sslState"
										type="text"
										label={gettext('State / Province / Region')}
										inputClassName="wds-input wds-input--md w-100"
										error={touched.sslState && errors.sslState}
										value={values.sslState}
										onChange={handleChange}
										onBlur={handleBlur}
										required
									/>
									<small>
										{gettext('Do not use abbreviations..')}
									</small>
								</div>
							</div>
							<FormikInput
								fieldType={fieldTypes.input}
								id="sslCity"
								type="text"
								className="w-50"
								inputClassName="w-50"
								label={gettext('City')}
								error={touched.sslCity && errors.sslCity}
								value={values.sslCity}
								onChange={handleChange}
								onBlur={handleBlur}
								required
							/>
							<FormikInput
								fieldType={fieldTypes.input}
								id="sslOrganizationName"
								type="text"
								containerClassName="w-100"
								inputClassName="w-100"
								label={gettext('Organization')}
								error={touched.sslOrganizationName && errors.sslOrganizationName}
								value={values.sslOrganizationName}
								onChange={handleChange}
								onBlur={handleBlur}
								required
							/>
							<FormikInput
								fieldType={fieldTypes.input}
								id="sslDepartmentName"
								type="text"
								containerClassName="w-100"
								inputClassName="w-100"
								label={gettext('Department')}
								error={touched.sslDepartmentName && errors.sslDepartmentName}
								value={values.sslDepartmentName}
								onChange={handleChange}
								onBlur={handleBlur}
								required
							/>
							<FormikInput
								fieldType={fieldTypes.input}
								id="sslEmail"
								type="text"
								className="w-100 mb-5"
								label={gettext('Technical Contact Email')}
								error={touched.sslEmail && errors.sslEmail}
								value={values.sslEmail}
								onChange={handleChange}
								onBlur={handleBlur}
								required
							/>
						</div>
					</div>
				)}
				<Box mt={3}>
					<Button buttonType="submit" disabled={disableSubmit} loading={isSubmitting}>
						{gettext('Send')}
					</Button>
				</Box>
			</FormGroup>
		</form>
	);
};

const fieldPropTypes = {
	firstName: PropTypes.string,
	lastName: PropTypes.string,
	email: PropTypes.string,
	subject: PropTypes.string,
	description: PropTypes.string,
	isRequestingPhone: PropTypes.bool,
	phoneNum: PropTypes.string,
	phoneExt: PropTypes.string,
	scheduleCallOption: PropTypes.string,
	scheduledDate1: PropTypes.string,
	scheduledDate2: PropTypes.string,
	scheduledDate3: PropTypes.string,
	scheduledTime1: PropTypes.string,
	scheduledTime2: PropTypes.string,
	scheduledTime3: PropTypes.string,
	relatedPrograms: PropTypes.string,
	relatedStages: PropTypes.string,
	relatedTasks: PropTypes.string,
	relatedUsers: PropTypes.string,
	relatedApplications: PropTypes.string,
	relatedReports: PropTypes.string,
	relatedAutomations: PropTypes.string,
	sslDomainName: PropTypes.string,
	sslCountry: PropTypes.string,
	sslState: PropTypes.string,
	sslCity: PropTypes.string,
	sslOrganizationName: PropTypes.string,
	sslDepartmentName: PropTypes.string,
	sslEmail: PropTypes.string,
};

HelpdeskForm.propTypes = {
	isEnterprisePlus: PropTypes.bool.isRequired,
	userType: PropTypes.string,
	requestType: PropTypes.string,
	categorization: PropTypes.string,
	browserVersion: PropTypes.string,
	canRequestCall: PropTypes.bool,
	successCallback: PropTypes.func,
	fieldsToDisplay: PropTypes.shape({
		nameFields: PropTypes.bool,
	}).isRequired,
	values: PropTypes.shape(fieldPropTypes).isRequired,
	touched: PropTypes.shape(
		Object.keys(fieldPropTypes).reduce((obj, field) => ({ ...obj, [field]: PropTypes.bool }), {}),
	).isRequired,
	errors: PropTypes.shape(
		Object.keys(fieldPropTypes).reduce((obj, field) => ({
			...obj,
			[field]: PropTypes.oneOfType([
				PropTypes.shape({ required: PropTypes.bool.isRequired }),
				PropTypes.string,
			]),
		}), {}),
	).isRequired,
	dirty: PropTypes.bool.isRequired,
	handleChange: PropTypes.func.isRequired,
	handleBlur: PropTypes.func.isRequired,
	handleSubmit: PropTypes.func.isRequired,
	isSubmitting: PropTypes.bool.isRequired,
	setFieldValue: PropTypes.func.isRequired,
	sslFields: PropTypes.bool.isRequired,
};

HelpdeskForm.defaultProps = {
	userType: '',
	requestType: '',
	categorization: '',
	browserVersion: '',
	successCallback: () => null,
	sslFields: false,
};

export default formikEnhancer(HelpdeskForm);
